"use client";

import { useMemo, useState } from "react";
import { useConvexAuth, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import {
  dockCoordinates,
  specToMatchArgs,
  type BuilderListing,
  type BuilderMatches,
  type BuilderSpec,
  type Dock,
} from "@/lib/listings/builder-state";

interface BuilderStateInput {
  productTypeSlug: string;
  initialSpec: BuilderSpec;
  initialMatches: BuilderMatches;
  initialQuantity: number;
}

function sameSpec(left: BuilderSpec, right: BuilderSpec): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function toggled(
  picks: Map<BuilderListing["_id"], BuilderListing>,
  listing: BuilderListing,
): Map<BuilderListing["_id"], BuilderListing> {
  const next = new Map(picks);
  if (next.has(listing._id)) next.delete(listing._id);
  else next.set(listing._id, listing);
  return next;
}

function usePicks() {
  const [picks, setPicks] = useState<Map<BuilderListing["_id"], BuilderListing>>(new Map());
  return {
    picks,
    clearPicks: () => setPicks(new Map()),
    togglePick: (listing: BuilderListing) => setPicks((current) => toggled(current, listing)),
  };
}

function hydratePicks(
  picks: Map<BuilderListing["_id"], BuilderListing>,
  live: BuilderMatches | undefined,
): BuilderListing[] {
  if (live) return live.listings.filter((listing) => picks.has(listing._id));
  return [...picks.values()];
}

function toggledCondition(conditions: string[], condition: string): string[] {
  return conditions.includes(condition)
    ? conditions.filter((value) => value !== condition)
    : [...conditions, condition];
}

export function useBuilderState({
  productTypeSlug,
  initialSpec,
  initialMatches,
  initialQuantity,
}: BuilderStateInput) {
  const [spec, setSpec] = useState<BuilderSpec>(initialSpec);
  const [quantity, setQuantity] = useState(initialQuantity);
  const [dock, setDock] = useState<Dock | null>(null);
  const [dockPending, setDockPending] = useState(false);
  const { isAuthenticated, isLoading: isAuthLoading } = useConvexAuth();
  const { picks, clearPicks, togglePick } = usePicks();
  const isInitial =
    sameSpec(spec, initialSpec) && dock === null && quantity === initialQuantity && picks.size === 0;
  const live = useQuery(
    api.products.categoryBuilderQueries.matchListings,
    isInitial && !isAuthenticated
      ? "skip"
      : {
          ...specToMatchArgs(productTypeSlug, spec),
          quantity,
          near: dockCoordinates(dock),
          pinned: [...picks.keys()],
        },
  );
  const isUpdating = live === undefined && (!isInitial || isAuthenticated || isAuthLoading);
  const matches = live ?? initialMatches;
  const picked = useMemo(() => hydratePicks(picks, live), [picks, live]);
  const update = (next: Partial<BuilderSpec>) => { setSpec((current) => ({ ...current, ...next })); clearPicks(); };
  const toggleGrade = (condition: string) =>
    update({ conditions: toggledCondition(spec.conditions, condition) });
  return {
    spec,
    update,
    toggleGrade,
    quantity,
    setQuantity,
    dock,
    setDock,
    dockPending,
    setDockPending,
    matches,
    isUpdating,
    picked,
    togglePick,
  };
}
